/**
 * 메시지 조작 — 결정 #16(스와이프/편집/분기). 모두 순수 함수로, 새 Message
 * 객체를 반환한다(불변). 저장(storage.putMessage)은 호출부(web)가 한다.
 *
 * 플랫폼 중립(결정 #10): 저장소/네트워크 의존 없음. ID 생성이 필요한 분기는
 * 팩토리(core)를 통해 새 ID를 만든다.
 */
import type { Chat, Message, MessageVariant } from "../domain/index.js";
import { newChatId, newMessageId } from "../util/id.js";
import { newSyncMeta } from "../util/sync-meta.js";

/** 현재 활성 variant의 텍스트를 편집한다(결정 #16: 제자리 수정 + 선택적 이력). */
export function editActiveVariant(
  message: Message,
  newContent: string,
  opts: { keepHistory?: boolean } = {},
): Message {
  const idx = message.activeVariant;
  const variants = message.variants.map((v, i) => {
    if (i !== idx) return v;
    const next: MessageVariant = { ...v, content: newContent };
    if (opts.keepHistory && v.content !== newContent) {
      next.editHistory = [
        ...(v.editHistory ?? []),
        { content: v.content, editedAt: Date.now() },
      ];
    }
    return next;
  });
  return { ...message, variants };
}

/**
 * 새 variant를 추가하고 그것을 활성으로 만든다(결정 #16: 재생성 스와이프).
 * 재생성 오케스트레이터가 생성한 텍스트를 이걸로 붙인다.
 */
export function addVariant(
  message: Message,
  variant: Partial<MessageVariant> & { content: string },
): Message {
  const v: MessageVariant = {
    content: variant.content,
    createdAt: variant.createdAt ?? Date.now(),
    ...(variant.model ? { model: variant.model } : {}),
    ...(variant.reasoning ? { reasoning: variant.reasoning } : {}),
  };
  const variants = [...message.variants, v];
  return { ...message, variants, activeVariant: variants.length - 1 };
}

/** 활성 variant 인덱스를 바꾼다(스와이프 좌/우). 범위를 벗어나면 클램프. */
export function selectVariant(message: Message, index: number): Message {
  if (message.variants.length === 0) return message;
  const clamped = Math.max(0, Math.min(index, message.variants.length - 1));
  if (clamped === message.activeVariant) return message;
  return { ...message, activeVariant: clamped };
}

/** 이전/다음 variant로 이동(없으면 그대로). */
export function swipe(message: Message, dir: -1 | 1): Message {
  return selectVariant(message, message.activeVariant + dir);
}

/**
 * 한 variant를 삭제한다. 마지막 하나는 지우지 않는다(메시지는 항상 variant ≥ 1,
 * 결정 #16). activeVariant는 안전하게 재조정된다.
 */
export function deleteVariant(message: Message, index: number): Message {
  if (message.variants.length <= 1) return message;
  if (index < 0 || index >= message.variants.length) return message;
  const variants = message.variants.filter((_, i) => i !== index);
  let active = message.activeVariant;
  if (active >= variants.length) active = variants.length - 1;
  else if (index < active) active -= 1;
  return { ...message, variants, activeVariant: Math.max(0, active) };
}

/**
 * 분기(결정 #16: 경량 세션 포크). 지정한 order까지의 메시지를 새 Chat으로 복제한
 * 결과를 반환한다. 메시지 단위 저장이라 행 복제가 저렴하다(결정 #9).
 * 호출부는 반환된 chat + messages를 저장하면 된다.
 *
 * @param atOrder 이 order "이하"의 메시지를 포함한다(해당 시점까지 복제).
 */
export function buildFork(
  source: Chat,
  messages: Message[],
  atOrder: number,
): { chat: Chat; messages: Message[] } {
  const forkChatId = newChatId();
  const chat: Chat = {
    ...source,
    id: forkChatId,
    sync: newSyncMeta(),
    title: `${source.title} (분기)`,
    forkedFrom: { chatId: source.id as Chat["id"], atOrder },
    lastMessageAt: Date.now(),
  };

  const included = messages
    .filter((m) => m.order <= atOrder && !m.sync.deletedAt)
    .sort((a, b) => a.order - b.order);

  const clonedMessages: Message[] = included.map((m) => ({
    ...m,
    id: newMessageId(),
    chatId: forkChatId,
    sync: newSyncMeta(),
    // variant 배열은 깊은 복사(편집 이력 공유 방지).
    variants: m.variants.map((v) => ({
      ...v,
      ...(v.editHistory ? { editHistory: [...v.editHistory] } : {}),
    })),
  }));

  chat.messageCount = clonedMessages.length;
  return { chat, messages: clonedMessages };
}
